/**
 * Wall-clock date and 15-minute bucket for proof-of-play at the screen
 * (from server receive time + player-reported `utcOffsetMinutes`).
 */

const MS_PER_DAY = 86_400_000
const MS_PER_MINUTE = 60_000
/** Proof-of-play rollup / event credit grain (15 minutes of wall clock at the screen). */
export const PROOF_OF_PLAY_WALL_BUCKET_MS = 15 * MS_PER_MINUTE

export interface TProofOfPlayWallClockFields {
  wallClockDate: number
  wallClockTimeBucket: number
}

/** Stable key for “has the on-screen 15-minute bucket rolled?” watches (date + bucket index). */
export function proofOfPlayWallBucketKey(
  receivedAt: Date,
  utcOffsetMinutes: number
): string {
  const wall = proofOfPlayWallClockFromUtcOffset(receivedAt, utcOffsetMinutes)
  return `${wall.wallClockDate}:${wall.wallClockTimeBucket}`
}

/**
 * Milliseconds until the next wall-clock 15-minute boundary at the screen.
 * When `now` sits exactly on a boundary, returns a full bucket (next boundary).
 */
export function msUntilNextProofOfPlayWallBucket(
  nowMs: number,
  utcOffsetMinutes: number
): number {
  if (!Number.isFinite(nowMs)) {
    return PROOF_OF_PLAY_WALL_BUCKET_MS
  }
  const wallMs = nowMs + utcOffsetMinutes * MS_PER_MINUTE
  let msIntoDay = wallMs % MS_PER_DAY
  if (msIntoDay < 0) {
    msIntoDay += MS_PER_DAY
  }
  const rem = msIntoDay % PROOF_OF_PLAY_WALL_BUCKET_MS
  if (rem === 0) {
    return PROOF_OF_PLAY_WALL_BUCKET_MS
  }
  return PROOF_OF_PLAY_WALL_BUCKET_MS - rem
}

/**
 * `receivedAt` is the server ingest instant (UTC). `utcOffsetMinutes` matches
 * `-new Date().getTimezoneOffset()` on the player (e.g. `-180` for GMT−3).
 */
export function proofOfPlayWallClockFromUtcOffset(
  receivedAt: Date,
  utcOffsetMinutes: number
): TProofOfPlayWallClockFields {
  const wallClockMs = receivedAt.getTime() + utcOffsetMinutes * MS_PER_MINUTE
  let msIntoDay = wallClockMs % MS_PER_DAY
  if (msIntoDay < 0) {
    msIntoDay += MS_PER_DAY
  }
  const minutesSinceMidnight = msIntoDay / MS_PER_MINUTE
  const wallClockTimeBucket = Math.min(95, Math.floor(minutesSinceMidnight / 15))
  const dayIndex = Math.floor(wallClockMs / MS_PER_DAY)
  const cal = new Date(dayIndex * MS_PER_DAY)
  const wallClockDate = cal.getUTCFullYear() * 10_000 + (cal.getUTCMonth() + 1) * 100 + cal.getUTCDate()
  return { wallClockDate, wallClockTimeBucket }
}

/** Add calendar days to a YYYYMMDD wall/UTC date int (proleptic Gregorian via UTC). */
export function proofOfPlayYyyymmddAddDays(dateInt: number, deltaDays: number): number {
  const y = Math.floor(dateInt / 10_000)
  const m = Math.floor((dateInt % 10_000) / 100)
  const d = dateInt % 100
  const dt = new Date(Date.UTC(y, m - 1, d + deltaDays))
  return dt.getUTCFullYear() * 10_000 + (dt.getUTCMonth() + 1) * 100 + dt.getUTCDate()
}

/**
 * UTC half-open interval for one wall 15-min grain (inverse of {@link proofOfPlayWallClockFromUtcOffset}).
 */
export function proofOfPlayWallBucketUtcRangeMs(
  wallClockDate: number,
  timeBucket: number,
  utcOffsetMinutes: number
): { startMs: number, endMsExclusive: number } {
  const y = Math.floor(wallClockDate / 10_000)
  const m = Math.floor((wallClockDate % 10_000) / 100)
  const d = wallClockDate % 100
  const bucket = Math.max(0, Math.min(95, Math.floor(timeBucket)))
  const wallMidnightAsUtcMs = Date.UTC(y, m - 1, d)
  const wallBucketStartAsUtcMs = wallMidnightAsUtcMs + bucket * PROOF_OF_PLAY_WALL_BUCKET_MS
  const startMs = wallBucketStartAsUtcMs - utcOffsetMinutes * MS_PER_MINUTE
  return { startMs, endMsExclusive: startMs + PROOF_OF_PLAY_WALL_BUCKET_MS }
}

/** True when the wall grain overlaps `[utcWindowStart, utcWindowEndExclusive)`. */
export function proofOfPlayWallBucketOverlapsUtcWindow(
  wallClockDate: number,
  timeBucket: number,
  utcOffsetMinutes: number,
  utcWindowStart: Date,
  utcWindowEndExclusive: Date
): boolean {
  const { startMs, endMsExclusive } = proofOfPlayWallBucketUtcRangeMs(
    wallClockDate,
    timeBucket,
    utcOffsetMinutes
  )
  return startMs < utcWindowEndExclusive.getTime() && endMsExclusive > utcWindowStart.getTime()
}

/** Human label e.g. `UTC+5:30` or `UTC−3` for management UI. */
export function formatUtcOffsetMinutesForDisplay(utcOffsetMinutes: number): string {
  if (utcOffsetMinutes === 0) {
    return 'UTC'
  }
  const sign = utcOffsetMinutes > 0 ? '+' : '−'
  const abs = Math.abs(utcOffsetMinutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  const body = m === 0 ? `${h}` : `${h}:${String(m).padStart(2, '0')}`
  return `UTC${sign}${body}`
}

/** Renders bucket index 0–95 as `HH:MM–HH:MM` (24h, local meaning is wall at screen). */
export function formatProofOfPlayTimeBucketRange(timeBucket: number): string {
  const b = Math.max(0, Math.min(95, Math.floor(timeBucket)))
  const startMin = b * 15
  const endMin = startMin + 15
  const fmt = (totalMin: number): string => {
    const h = Math.floor(totalMin / 60) % 24
    const m = totalMin % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }
  return `${fmt(startMin)}–${fmt(endMin)}`
}

/** Start of the 15-minute bucket only (`HH:MM`), for compact labels and short tooltips. */
export function formatProofOfPlayTimeBucketStart(timeBucket: number): string {
  const b = Math.max(0, Math.min(95, Math.floor(timeBucket)))
  const startMin = b * 15
  const h = Math.floor(startMin / 60) % 24
  const m = startMin % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
